/**
 * Google reCAPTCHA v3 client helper. Canonical SakthiInc pattern —
 * mirrors scribenza/budgeza/mathacad/tabowe/stovalt/projectrax utils.
 *
 * Site key + enabled flag are fetched from
 * {VITE_API_URL}/api/config/security on first use (fabrinza mounts
 * routes at /api via Laravel's default prefix and VITE_API_URL is
 * bare-domain). When disabled, executeRecaptcha() resolves to null
 * so callers can pass `recaptcha_token: null` — backend skips verify.
 */

let configPromise = null
let scriptPromise = null
let cachedConfig = null

async function loadConfig() {
  if (cachedConfig) return cachedConfig
  if (!configPromise) {
    const base = (import.meta.env.VITE_API_URL || '').replace(/\/+$/, '')
    configPromise = fetch(`${base}/api/config/security`, {
      headers: { Accept: 'application/json' },
    })
      .then((r) => r.json())
      .then((j) => j?.recaptcha || { enabled: false, site_key: null })
      .catch(() => ({ enabled: false, site_key: null }))
  }
  cachedConfig = await configPromise
  return cachedConfig
}

function loadScript(siteKey) {
  if (scriptPromise) return scriptPromise
  scriptPromise = new Promise((resolve, reject) => {
    if (window.grecaptcha?.execute) return resolve()
    const s = document.createElement('script')
    s.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`
    s.async = true
    s.defer = true
    s.onload = () => {
      window.grecaptcha?.ready
        ? window.grecaptcha.ready(resolve)
        : resolve()
    }
    s.onerror = () => reject(new Error('Failed to load reCAPTCHA'))
    document.head.appendChild(s)
  })
  return scriptPromise
}

export async function executeRecaptcha(action) {
  const cfg = await loadConfig()
  if (!cfg.enabled || !cfg.site_key) return null
  try {
    await loadScript(cfg.site_key)
    return await window.grecaptcha.execute(cfg.site_key, { action })
  } catch (e) {
    console.warn('[recaptcha] execute failed', e)
    return null
  }
}

export function resetRecaptchaConfig() {
  configPromise = null
  scriptPromise = null
  cachedConfig = null
}
